// Ported from dev-scrips-pm-react src/documents/services/use-lab-order-codes.ts (PROD-1119 documents module).
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * GET /api/Master/LabOrderCodesList?searchQuery={q}&system={system}
 *
 * Master-data typeahead for lab tests. Used by the Labs document
 * sub-form (PROD-797 PR 3) to populate `patientDocumentLabs[].labName`.
 *
 * Wire-shape dev-curl verified 2026-05-15 by Andrew:
 * ```json
 * [
 *   {
 *     "Id": "2d8f4180-1a8a-ef11-b495-0022480d88b8",
 *     "code": {
 *       "code": "30020",
 *       "display": "Urinalysis with Microscopic Analysis",
 *       "system": "IHL"
 *     }
 *   },
 *   ...
 * ]
 * ```
 *
 * Wire-shape gotchas:
 *   - Top-level `Id` is CAPITALISED (.NET serializer default for the
 *     master tables). Inner `code` object is lowercased.
 *   - Inner `code` is the canonical shape downstream: it matches
 *     `patientDocumentLabs[].labName` on the document body verbatim
 *     (lowercase `code` / `display` / `system`). Consumers can ship
 *     `result.code` directly as `labName` with NO translation.
 *
 * `system` query param is `IHL` for the live curl. This is the lab
 * catalogue the Scrips org uses; other orgs could ship different
 * catalogue systems. We default to `IHL` for V1 (Andrew's curl) and
 * accept an override if the consumer needs something else.
 *
 * Empty / very-short queries: the catalogue is sizable, so we
 * short-circuit `enabled: false` until the user types at least one
 * character. Same pattern as `useProcedureCodesSearch`.
 *
 * @see {@link ../../../docs/CONVERSION_NOTES_PROD-797.md} (PR 3 wire-shape gotcha table)
 */

export interface LabOrderCode {
  Id: string;
  /** Wire-canonical shape — keys are lowercase; matches the document
   *  body's `patientDocumentLabs[].labName` field exactly. */
  code: {
    code?: string;
    display?: string;
    system?: string;
  };
}

interface UseLabOrderCodesParams {
  searchQuery: string;
  /** Catalogue system. Defaults to `IHL` (Scrips org's standard). */
  system?: string;
  /** Optional explicit gate (e.g. don't fire when a parent form is closed). */
  enabled?: boolean;
}

export function useLabOrderCodes(params: UseLabOrderCodesParams) {
  const { searchQuery, system = 'IHL', enabled = true } = params;
  const trimmed = searchQuery.trim();
  return useQuery<LabOrderCode[]>({
    queryKey: ['lab-order-codes', system, trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed, system });
      return get<LabOrderCode[]>(
        `/api/Master/LabOrderCodesList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: enabled && trimmed.length > 0,
    // Master catalogues are stable; cache an hour. Same convention
    // as the other master-data hooks (`useProcedureCodesSearch`).
    staleTime: 60 * 60 * 1000,
  });
}
